import { ImmutableMap, Prop, QueryParams, RequiredOnly, isNumber, isObject, isString } from './utility.model';

export const PRESSURE_UNIT = ['psi', 'kg_cm2', 'mpa', 'bar'] as const;
export type PressureUnit = (typeof PRESSURE_UNIT)[number];
export const TEMPERATURE_UNIT = ['celsius', 'fahrenheit'] as const;
export type TemperatureUnit = (typeof TEMPERATURE_UNIT)[number];

type OrganizationRequiredProps = {
  name: string;
  consentFormId: string;
  pressureUnit: PressureUnit;
  temperatureUnit: TemperatureUnit;
};

type OrganizationOptionalProps = {
  remarks?: string;
};

type OrganizationProvidedProps = {
  organizationId: string;
  requireMfa: boolean;
  departmentNames: string[];
  notificationRecipients: string[];
  consentFormName: string;
  updatedAt: number;
  version: number;
};

export type OrganizationQueryParams = QueryParams;

export type OrganizationCreateParams = OrganizationRequiredProps & OrganizationOptionalProps;

export type OrganizationUpdateParams = Partial<OrganizationRequiredProps> & OrganizationOptionalProps;

export type Organization = OrganizationRequiredProps & OrganizationOptionalProps & OrganizationProvidedProps;

export function isOrganizationCreateParams(value: unknown): value is OrganizationCreateParams {
  const sProps: Prop<RequiredOnly<OrganizationCreateParams>, string>[] = ['name', 'consentFormId', 'pressureUnit', 'temperatureUnit'];
  return isObject(value) && sProps.every(prop => isString(value[prop]));
}

export function isOrganizationUpdateParams(value: unknown): value is OrganizationUpdateParams {
  const sProps: Prop<OrganizationUpdateParams, string>[] = ['name', 'consentFormId', 'pressureUnit', 'temperatureUnit', 'remarks'];
  return isObject(value) && sProps.some(prop => isString(value[prop]));
}

export function isOrganization(value: unknown): value is Organization {
  const sProps: Prop<RequiredOnly<Organization>, string>[] = ['organizationId', 'name'];
  const nProps: Prop<RequiredOnly<Organization>, number>[] = ['updatedAt', 'version'];
  return isObject(value) && sProps.every(prop => isString(value[prop])) && nProps.every(prop => isNumber(value[prop]));
}

export class Organizations extends ImmutableMap<string, Organization> {
  constructor(organizations?: Organization[] | Iterable<Organization>) {
    super((organization: Organization) => organization.organizationId, organizations);
  }

  protected clone(organizations?: Organization[] | Iterable<Organization>): this {
    return new Organizations(organizations) as this;
  }
}
